<template>
  <div class="schedule-wrapper">
    <h2 class="section-title">{{ this.studentName }}</h2>
    <h3 class="section-title">{{ $t(`children.calendarTitle`) }}</h3>

    <v-card class="calendar-container" elevation="2">
      <v-overlay :value="isTableLoading" absolute>
        <v-progress-circular indeterminate color="primary" size="48" />
      </v-overlay>
      <div class="calendar-toolbar-card">
        <div class="calendar-nav">
          <v-btn icon @click="prevWeek"
            ><v-icon>mdi-chevron-left</v-icon></v-btn
          >
          <span class="calendar-title">
            {{ formattedDateRange }}
          </span>
          <v-btn icon @click="nextWeek"
            ><v-icon>mdi-chevron-right</v-icon></v-btn
          >
        </div>

        <v-text-field
          v-model="searchQuery"
          :label="$t(`children.searchLabel`)"
          dense
          outlined
          hide-details
          class="search-input"
        ></v-text-field>
      </div>

      <div class="calendar-grid">
        <div class="calendar-time">
          <div class="time-slot" v-for="hour in hours" :key="hour">
            {{ hour }}:00
          </div>
        </div>

        <div class="calendar-days">
          <div
            class="day-header"
            :class="{ 'weekend-header': isWeekend(day) }"
            v-for="(day, index) in currentWeek"
            :key="index + day"
          >
            {{ formatDate(day) }}
          </div>

          <div
            class="day-column"
            :class="{ 'weekend-column': isWeekend(day) }"
            v-for="(day, index) in currentWeek"
            :key="index"
          >
            <div class="day-grid">
              <template v-for="hour in hours">
                <div
                  v-if="!eventExists(day, hour)"
                  class="day-slot"
                  :class="{ 'weekend-slot': isWeekend(day) }"
                  :key="hour + day"
                ></div>
              </template>
              <div
                v-for="event in filteredEvents(day)"
                :key="event.id"
                class="event-card"
                :class="{ dimmed: searchQuery && !matchesSearch(event),
                  'weekend-event': isWeekend(day) 
                }"
                :style="eventStyle(event)"
              >
                <strong>{{ event.name }}</strong>
                <span>{{ event.teacher }}</span>
                <span class="status">{{ event.status }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isCurrentTimeVisible"
        class="current-time-line"
        :style="{ top: currentTimePosition + 'px' }"
      ></div>
    </v-card>
    
    <div class="schedule-acts-wrapper">
      <h3 class="section-title">{{ $t(`children.acts`) }}</h3>

      <v-card class = 'calendar-container'>
        <v-data-table
          :headers="headers"
          :items="acts"
          class="elevation-1"
          hide-default-footer
          :mobile-breakpoint="0"
        >
          <template slot="item" slot-scope="{ index, item }">
            <tr @click="goToActs(item.actNumber)">
              <td>{{ index + 1 }}</td>
              <td>{{ item.actNumber }}</td>
              <td>{{ item.course }}</td>
              <td>{{ item.date }}</td>
              <td>{{ item.teacher }}</td>
              <td>
                <v-btn :color="item.status_color" small outlined>
                  {{ item.status }}
                </v-btn>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>
  
  </div>
</template>

<script>
import { format, addDays, subDays } from "date-fns";
import { kk, ru } from "date-fns/locale";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "Schedule",
  data() {
    return {
      searchQuery: "",
      isCurrentTimeVisible: false,
      currentTimePosition: 0,
      isTableLoading: false,
      currentDate: new Date(),
      hours: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18],
      events: [],
      studentName: null,
      headers: [
        { text: "№", value: "index", align: "start", sortable: false },
        { text: this.$t("children.actNumber"), value: "actNumber" },
        { text: this.$t("children.courseName"), value: "course" },
        { text: this.$t("children.data"), value: "date" },
        { text: this.$t("children.teacher"), value: "teacher" },
        { text: this.$t("children.status"), value: "actions", sortable: false },
      ],
    };
  },
  created() {
    const { start_date, end_date } = this.dateRangeForQuery;
    this.initializePageData(start_date, end_date);
  },
  computed: {
    ...mapGetters("children", ["childInfo"]),
    ...mapGetters("acts", ["acts"]),
    dateFnsLocale() {
      const localeMap = {
        kk: kk,
        ru: ru,
      };
      return localeMap[this.$i18n.locale] || ru;
    },
    currentWeek() {
      if (window.innerWidth <= 480) {
        return Array.from({ length: 3 }, (_, i) => addDays(this.currentDate, i));
      }
      return Array.from({ length: 5 }, (_, i) => addDays(this.currentDate, i));
    },
    formattedDateRange() {
      const start = format(this.currentWeek[0], "dd MMMM", {
        locale: this.dateFnsLocale,
      });
      const end = format(
        this.currentWeek[this.currentWeek.length - 1],
        "dd MMMM, yyyy",
        { locale: this.dateFnsLocale }
      );
      return `${start} - ${end}`;
    },
    dateRangeForQuery() {
      const start_date = format(this.currentWeek[0], "yyyy-MM-dd");
      const end_date = format(
        this.currentWeek[this.currentWeek.length - 1],
        "yyyy-MM-dd"
      );
      return { start_date, end_date };
    },
  },
  methods: {
    ...mapActions({
      fetchChild: "children/loadChild",
      fetchActs: "acts/loadActs",
    }),
    async initializePageData(start_date, end_date) {
      this.isTableLoading = true;

      const query = {
        start_date,
        end_date,
        child_id: this.$route.params.childId,
      };

      try {
        const childPromise = this.fetchChild(query);
        const actsPromise = this.fetchActs(query.child_id);
        
        const childResult = await childPromise;
        this.studentName = childResult.student_name;
        this.events = this.transformScheduleData(childResult.diaries);
      } catch (error) {
        this.$toast.error(`Ошибка при загрузке данных: ${error}`, {
          position: "top-right",
        });
      } finally {
        this.isTableLoading = false;
      }
    },
    isWeekend(day) {
      const dayOfWeek = day.getDay();
      return dayOfWeek === 0 || dayOfWeek === 6;
    },
    transformScheduleData(rawData) {
      const colors = ["#9fe6a0", "#f28b82", "#d7aefb", "#aecbfa", "#fdd835"];

      return rawData.map((item, index) => {
        const [startHour, startMinutes] = item.time_start
          .split(":")
          .map(Number);
        const [endHour, endMinutes] = item.time_end.split(":").map(Number);

        const start = startHour + startMinutes / 60;
        const end = endHour + endMinutes / 60;
        const duration = end - start;

        const date = new Date(item.date);
        const day = date.getDay();

        return {
          id: index + 1,
          name: item.name,
          teacher: item.teacher,
          day: day,
          hour: Math.floor(start),
          duration: Math.max(1, Math.round(duration)),
          color: colors[Math.floor(Math.random() * colors.length)],
          status: item.status,
        };
      });
    },
    goToActs(actId) {
      this.$router.push({
        name: "Acts",
        params: { actId, childId: this.$route.params.childId },
      });
    },
    updateCurrentTime() {
      this.$nextTick(() => {
        const now = new Date();
        const hour = now.getHours();
        const minutes = now.getMinutes();
        const startHour = 9;
        const endHour = 18;
        const hourHeight = 80;
        const minuteHeight = hourHeight / 60;

        if (hour >= startHour && hour <= endHour) {
          const hoursPassed = hour - startHour;
          const timeOffset = hoursPassed * hourHeight + minutes * minuteHeight;

          const grid = this.$el.querySelector(".day-grid");
          const gridTop = grid?.getBoundingClientRect()?.top || 0;
          const containerTop =
            this.$el
              .querySelector(".calendar-container")
              ?.getBoundingClientRect()?.top || 0;

          const offsetTop = gridTop - containerTop;

          this.currentTimePosition = offsetTop + timeOffset;
          this.isCurrentTimeVisible = true;
        } else {
          this.isCurrentTimeVisible = false;
        }
      });
    },
    eventExists(day, hour) {
      return this.events.some(
        (event) =>
          event.day === day.getDay() &&
          event.hour <= hour &&
          event.hour + event.duration > hour
      );
    },
    fetchWeekData() {
      const { start_date, end_date } = this.dateRangeForQuery;
      this.initializePageData(start_date, end_date);
    },
    prevWeek() {
      const daysToMove = window.innerWidth <= 480 ? 3 : 5;
      this.currentDate = subDays(this.currentDate, daysToMove);
      this.fetchWeekData();
      this.updateCurrentTime();
    },
    nextWeek() {
      const daysToMove = window.innerWidth <= 480 ? 3 : 5;
      this.currentDate = addDays(this.currentDate, daysToMove);
      this.fetchWeekData();
      this.updateCurrentTime();
    },
    formatDate(date) {
      if (window.innerWidth <= 480) {
        const dayNumber = format(date, 'dd');
        const monthShort = format(date, 'MMM', { locale: this.dateFnsLocale });
        const dayShort = format(date, 'EEEEEE', { locale: this.dateFnsLocale }).toUpperCase();
        
        return `${dayNumber} ${monthShort}, ${dayShort}`;
      }
      
      const dayShort = format(date, 'EEEEEE', { locale: this.dateFnsLocale }).toUpperCase();
      const dateShort = format(date, 'dd MMM', { locale: this.dateFnsLocale });
      
      return `${dateShort}, ${dayShort}`;
    },
    filteredEvents(day) {
      return this.events.filter((event) => event.day === day.getDay());
    },
    matchesSearch(event) {
      return event.name.toLowerCase().includes(this.searchQuery.toLowerCase());
    },
    eventStyle(event) {
      const startRow = this.hours.indexOf(event.hour) + 1;
      const endRow = startRow + event.duration;

      const padding = window.innerWidth <= 480 ? '4px' : '8px';
      const fontSize = window.innerWidth <= 480 ? '10px' : '12px';
      
      return {
        "grid-row-start": startRow,
        "grid-row-end": endRow,
        border: `3px solid ${event.color}`,
        borderColor: event.color,
        background: this.lightenColor(event.color, 0.85),
        padding: padding,
        fontSize: fontSize
      };
    },
    lightenColor(hex, percent) {
      hex = hex.replace("#", "");

      let num = parseInt(hex, 16);
      let r = (num >> 16) & 255;
      let g = (num >> 8) & 255;
      let b = num & 255;

      r = Math.min(255, Math.max(0, Math.round(r + (255 - r) * percent)));
      g = Math.min(255, Math.max(0, Math.round(g + (255 - g) * percent)));
      b = Math.min(255, Math.max(0, Math.round(b + (255 - b) * percent)));

      return `rgb(${r}, ${g}, ${b})`;
    },
  },
  mounted() {
    this.updateCurrentTime();

    const now = new Date();
    const hour = now.getHours();
    const startHour = 9;
    const endHour = 18;

    if (hour >= startHour && hour <= endHour) {
      this.timer = setInterval(() => {
        this.updateCurrentTime();
      }, 60 * 1000);
    }
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
};
</script>

<style scoped>
.schedule-wrapper {
  font-family: "Montserrat", sans-serif;
}
.schedule-wrapper h2,
h3 {
  font-family: "Montserrat Alternates", sans-serif;
}
.calendar-container {
  padding: 20px;
  border-radius: 12px;
}

.calendar-toolbar-card {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 10px 20px;
}

.search-input {
  max-width: 200px;
}

.calendar-nav {
  display: flex;
  align-items: center;
  font-weight: bold;
}

.calendar-title {
  margin: 0 10px;
}

.calendar-grid {
  display: flex;
  margin-top: 10px;
  position: relative;
}

.calendar-time {
  display: flex;
  flex-direction: column;
  width: 80px;
  align-items: center;
  padding-right: 10px;
  justify-content: end;
}

.time-slot {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.calendar-days {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  flex-grow: 1;
}

.day-header {
  text-align: center;
  font-weight: bold;
  padding: 10px;
  border-bottom: 1px solid #ccc;
}

.day-column {
  display: flex;
  flex-direction: column;
}

.day-grid {
  display: grid;
  grid-template-rows: repeat(10, 80px);
  position: relative;
}

.day-slot {
  border: 1px solid #ddd;
}

.event-card {
  width: 100%;
  padding: 8px;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  flex-direction: column;
  justify-content: center;
  transition: opacity 0.3s ease-in-out;
}
.event-card .status {
  display: flex;
  justify-content: end;
}
.dimmed {
  opacity: 0.2;
}
.schedule-acts-wrapper {
  margin-top: 50px;
}
::v-deep .v-data-table-header th {
  font-weight: bold;
  color: #272833 !important;
}
::v-deep .v-data-table__wrapper tr {
  cursor: pointer;
}
::v-deep .v-data-table__wrapper tr td button {
  font-weight: bold;
  text-transform: none;
}
.current-time-line {
  position: absolute;
  left: 43px;
  right: 0;
  height: 2px;
  background-color: blue;
}
.current-time-line::before {
  content: "";
  width: 12px;
  height: 12px;
  background-color: blue;
  border-radius: 50%;
  position: absolute;
  left: -6px;
  top: -5px;
}

.weekend-header {
  color: #d32f2f;
}

.weekend-column {
  background-color: rgba(255, 0, 0, 0.05);
}

.weekend-slot {
  background-color: rgba(255, 0, 0, 0.03);
  border-color: rgba(255, 0, 0, 0.1);
}

.weekend-event {
  opacity: 0.95;
}

@media (max-width: 480px) {
  .schedule-wrapper {
    padding: 0 30px;
  }
  
  .section-title {
    text-align: center;
  }
  
  .calendar-toolbar {
    display: flex !important;
    flex-direction: column !important;
  }
  
  .search-input {
    width: 100%;
    max-width: none;
  }
  
  .calendar-nav {
    justify-content: center;
  }
  
  .calendar-days {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .calendar-time {
    width: 60px;
    padding-right: 5px;
  }
  
  .time-slot {
    height: 80px;
    font-size: 12px;
    padding: 5px 0;
  }
  
  .day-header {
    padding: 8px 5px;
    font-size: 13px;
    line-height: 1.2;
  }
  
  .event-card {
    padding: 4px;
    font-size: 10px;
    line-height: 1.1;
  }
  
  .event-card strong {
    font-size: 10px;
    margin-bottom: 2px;
  }
  
  .event-card span {
    font-size: 9px;
    margin-bottom: 1px;
  }
  
  .event-card .status {
    margin-top: 2px;
    font-size: 9px;
  }
  
  .calendar-title {
    font-size: 14px;
    text-align: center;
    min-width: 200px;
  }
  
  .current-time-line {
    left: 30px;
  }
}

@media (max-width: 360px) {
  .calendar-days {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .calendar-time {
    width: 50px;
  }
  
  .time-slot {
    font-size: 11px;
    height: 75px;
  }
  
  .day-header {
    font-size: 12px;
    padding: 6px 3px;
  }
  
  .calendar-title {
    font-size: 13px;
    min-width: 180px;
  }
}
</style>